/* eslint-disable max-lines */
/************************************************/
/*          About dayjs                         */
/************************************************/
// The MIT License (MIT)
//
// Copyright (c) 2018-present, iamkun
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.

/************************************************/
/*          About Chart.js                      */
/************************************************/
// The MIT License (MIT)
//
// Copyright (c) 2014-2024 Chart.js Contributors
//
// Permission is hereby granted, free of charge, to any person obtaining a copy of
// this software and associated documentation files (the "Software"),
// to deal in the Software without restriction, including without limitation the rights to use,
// copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software,
// and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
// INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
// IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY,
// WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

/************************************************/
/*          About chartjs-adapter-dayjs-4       */
/************************************************/
// The MIT License (MIT)
//
// Copyright (c) 2019 Chart.js Contributors
//
// Permission is hereby granted, free of charge, to any person obtaining a copy of
// this software and associated documentation files (the "Software"), to deal in the Software without restriction,
// including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense,
// and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
// INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
// IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY,
// WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

/************************************************/
/*    About chartjs/chartjs-plugin-annotation   */
/************************************************/
// The MIT License (MIT)

// Copyright (c) 2016-2021 chartjs-plugin-annotation Contributors

// Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
// The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

/************************************************/
/*    About chartjs/chartjs-plugin-zoom         */
/************************************************/
// The MIT License (MIT)

// Copyright (c) 2013-2024 chartjs-plugin-zoom contributors

// Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
// The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

import dayjs from 'dayjs';
import Chart from 'chart.js/auto';
import 'chartjs-adapter-dayjs-4/dist/chartjs-adapter-dayjs-4.esm';
import zoomPlugin from 'chartjs-plugin-zoom';
import annotationPlugin from 'chartjs-plugin-annotation';
import { GraphMediator, DEFAULT_ZOOM_PAN_MODE } from './GraphLibrary';
import { binarySearch } from './logicUtils';
import c from '../config';
import { fixNumberWithDecimal } from "@/common/nodejs/mathFunctions";

const MAX_CHAR = 16;
const ONE_SECOND = 1;

const TIME_DIGITS = 13;

// UNIXタイムスタンプ
const UTC = dayjs.unix(0);

// グラフの線の太さ
export const LINE_WIDTH = {
  THIN: 1,
  NORMAL: 2,
  THICK: 3
};
// グラフの線のスタイル
export const STYLE = {
  SOLID: 1,
  DASHED: 2,
  DOTTED: 3
};

export const THIN = 0.4; // 細い
export const NORMAL = 0.8;  // 普通
export const THICK = 5.2;  // 太い
export const SOLID = []; // 実線
export const DASHED = [5, 2]; // 破線
export const DOTTED = [2, 2]; // 点線
// グラフで使用する固定色
export const COLOR = {
  DEFAULT: 'rgba(0, 0, 0, 1)',
  CANVAS_BACK_GROUND: 'rgba(255, 255, 255, 1)',
  CANVAS_FRAME: 'rgba(0, 0, 0, 0.5)',
  GRID: 'rgba(169, 169, 169, 0.7)',
};

// コンポーネントの更新種別
export const UPDATE_TYPE = {
  X_MIN_MAX: 'x_min_max', // x軸の最大と最小の更新
  X_TICKS: 'x_ticks'      // x軸のTICKSの更新
};

export const LEGEND_DEFS = Object.freeze({
  MAX: 'max', MIN: 'min', AVG: 'avg', VALUE_A: 'valueA', VALUE_B: 'valueB'
});

const CURSOR_ICON = Object.freeze({
  [c.CURSOR.A]: '\u24B6',
  [c.CURSOR.B]: '\u24B7',
});

const DEFAULT_CURSOR_SETTINGS = Object.freeze({
  [c.CURSOR.A]: { xPosition: null, time: null, display: CURSOR_ICON[c.CURSOR.A] },
  [c.CURSOR.B]: { xPosition: null, time: null, display: CURSOR_ICON[c.CURSOR.B] }
});

let containerHeight = 0;

const PADDING_GRAPH_DISPLAY = 6;
// ドラッグとクリックの判定に使用する移動距離の閾値(px)
const CLICK_DRAG_THRESHOLD_PX = 4;
const SINGLE_CLICK_DELAY_MS = 200; // シングルクリックとダブルクリックを判定するための遅延時間

/**
 * グラフ操作の種類
 */
const INTERACTION_TYPE = Object.freeze({
  ZOOM: 'zoom',
  PAN: 'pan'
});

export class Graph extends HTMLElement {
  // コンストラクタ
  // 必ず親コンストラクタ super() を呼び出すこと。
  // 属性値 _name を初期化
  constructor() {
    super();
    this._id = null;
    this._xAxesInfo = {};
    this._yAxesInfo = {};
    this._graphData = null;
    this._graphComponent = null;
    this.xMin = UTC;
    this.xMax = this.xMin; // x軸の最大表示範囲
    this.currentTime = this.xMin; // 現在再生時刻
    this.rangeMs = 0;
    this.startTime = UTC; // グラフの開始時間(dayjs)
    this.endTime = UTC; // グラフの終了時間(dayjs)
    this.markerLine = [];
    this.cursorLine = { ...DEFAULT_CURSOR_SETTINGS }; // カーソル情報。新規オブジェクトとして代入して参照を切る
    this.cursorItem = null; // 1つのグラフに複数項目描画している場合に、どの項目がカーソル項目か保持する
    this.datasetList = [];
    this.index = undefined; // 分割グラフ用インデックス
    this.dataAppending = false; // データ投入中フラグ
    this.stepSize = ONE_SECOND; // デフォルトは1秒ごとに設定
    this.baseTime = null; // 計測開始時間
    this.baseTimeOffset = null; // 計測開始時間のオフセット
    this.graphScaleMinXPixel = null;
    this.translatedValue = {
      [LEGEND_DEFS.MAX]: '', [LEGEND_DEFS.MIN]: '', [LEGEND_DEFS.AVG]: '', [LEGEND_DEFS.VALUE_A]: '', [LEGEND_DEFS.VALUE_B]: ''
    };
    this.draggingElement = null; // ドラッグ操作中のelement
    this.lastEvent = null; // ドラッグした最後のevent
    this.draggingCallback = () => { }; // ドラッグ時に実行するコールバック関数
    this.zoomPanSyncCallback = () => { }; // ズーム/パン完了時に実行するコールバック関数
    this.resetZoomSyncCallback = () => { }; // ダブルクリック時のズームリセットで実行するコールバック関数
    this.currentTimeSelectedCallback = () => { }; // グラフクリック時に実行するコールバック関数
    this.clickTimer = null; // ダブルクリック判定用タイマー
    this.pointerDownPosition = null;
    this.ignoreNextClick = false;

    this.onCanvasMouseDown = (event) => {
      this.pointerDownPosition = { x: event.clientX, y: event.clientY };
      this.ignoreNextClick = false;
    };
    this.onCanvasMouseMove = (event) => {
      if (!this.pointerDownPosition) {
        return;
      }

      const diffX = Math.abs(event.clientX - this.pointerDownPosition.x);
      const diffY = Math.abs(event.clientY - this.pointerDownPosition.y);
      if (diffX > CLICK_DRAG_THRESHOLD_PX || diffY > CLICK_DRAG_THRESHOLD_PX) {
        // ドラッグと判定された場合、次のクリックイベントを無視する(ドラッグ完了時のクリックイベントを防止するため)
        this.ignoreNextClick = true;
      }
    };
    this.onCanvasMouseUp = () => {
      this.pointerDownPosition = null;
    };
    this.onCanvasMouseLeave = () => {
      this.pointerDownPosition = null;
    };

    this.onCanvasClick = (event) => {
      // ダブルクリック時にクリック処理が発火しないよう遅延実行する
      clearTimeout(this.clickTimer);
      this.clickTimer = setTimeout(() => {
        if (this.ignoreNextClick) {
          this.ignoreNextClick = false;
          return;
        }
        this.handleGraphClick(event);
      }, SINGLE_CLICK_DELAY_MS);
    };
    this.onCanvasDoubleClick = () => {
      // クリックのタイマーをキャンセルしてからダブルクリック処理を実行する
      clearTimeout(this.clickTimer);
      this.handleDoubleClickResetZoom();
    };

    this.eventCanvas = null;
    // 同期ループ防止用の状態
    // null          : 同期適用待ちではない状態（初期化時／同期起因でない完了イベント後／判定処理後）
    // { xMin, xMax }: 同期適用直後。次の完了イベントで現在のスケールと比較し、同期起因かを判定する
    this.suppressedZoomPanRange = null;
    this.zoomPanMode = DEFAULT_ZOOM_PAN_MODE; // chartjs-plugin-zoom に渡すズーム/パンの軸モード
    this.zoomPanStateChangedCallback = () => { }; // ズーム/パン状態の変更時に実行するコールバック関数

    // グラフの背景色を設定するプラグイン
    this.PLUGIN_DESIGN = {
      id: 'custom_canvas_background_color',
      beforeDraw: (chart) => {
        const ctx = chart.canvas.getContext('2d');
        ctx.save();
        ctx.globalCompositeOperation = 'destination-over';
        ctx.fillStyle = COLOR.CANVAS_BACK_GROUND;
        ctx.setLineDash([0]);
        // 四角形描画
        ctx.fillRect(chart.scales.x.left, chart.scales["yaxis_0"].top - PADDING_GRAPH_DISPLAY, chart.scales.x.width, chart.scales["yaxis_0"].bottom + 4);
        ctx.strokeStyle = COLOR.CANVAS_FRAME; // 外枠描画を追加(CITS)
        ctx.strokeRect(chart.scales.x.left, chart.scales["yaxis_0"].top - 8, chart.scales.x.width, 1); // 上の外枠描画(CITS)
        ctx.strokeRect(chart.scales.x.left, chart.scales["yaxis_0"].bottom + PADDING_GRAPH_DISPLAY, chart.scales.x.width, 1); // 下の外枠描画(CITS)
        ctx.restore();

        if (chart.options.responsive && this.dataAppending) {
          // 背景の設定後、負荷が大きいため計測中はグラフのレスポンシブを止める
          chart.options.responsive = false;
          chart.update();
        }
      },
    };

    // カレントラインを引くためのプラグイン
    this.PLUGIN_CURRENT_LINE = {
      id: 'currentLine',
      afterDraw: (chart) => {
        const ctx = chart.canvas.getContext('2d');
        const { enabled, xPosition } = chart.options.plugins.currentLine;

        // 実行対象外の場合は処理中止
        if (!enabled || !xPosition) {
          return;
        }

        ctx.save();
        ctx.strokeStyle = 'blue';
        ctx.lineWidth = 3;

        ctx.beginPath();
        // グラフの上下を広げた分、カレントラインの高さも広げる
        ctx.moveTo(xPosition, this._graphComponent.chartArea.top - PADDING_GRAPH_DISPLAY);
        ctx.lineTo(xPosition, this._graphComponent.chartArea.bottom + PADDING_GRAPH_DISPLAY);
        ctx.stroke();

        ctx.restore();
      }
    };

    // マーカラインを引くためのプラグイン
    this.PLUGIN_MARKER_LINE = {
      id: 'markerLine',
      afterDraw: (chart) => {
        const ctx = chart.canvas.getContext('2d');
        const { enabled, xPositions } = chart.options.plugins.markerLine;

        // 実行対象外の場合は処理中止
        if (!enabled || !xPositions) {
          return;
        }
        // マーカラインを描画する直前にマーカーラインの位置を更新する
        this.updateMarker();
        this.drawSetMarkerMain();
        const newXPositions = chart.options.plugins.markerLine.xPositions;
        const cursor = chart.options.plugins.cursorLine;

        ctx.save();
        ctx.strokeStyle = 'red';
        ctx.lineWidth = 3;

        for (const xPosition of newXPositions) {
          ctx.beginPath();
          // グラフの上下を広げた分、マーカーラインの高さも広げる
          ctx.moveTo(xPosition, this._graphComponent.chartArea.top - PADDING_GRAPH_DISPLAY);
          ctx.lineTo(xPosition, this._graphComponent.chartArea.bottom + PADDING_GRAPH_DISPLAY);
          ctx.stroke();
        }

        if (!cursor.enabled) {
          return;
        }
        // カーソル線上部のアイコン描画
        for (const cursorKey of Object.keys(cursor)) {
          if (!cursor[cursorKey].xPosition) {
            continue;
          }

          ctx.beginPath();
          ctx.font = '20px "Helvetica Neue", Helvetica, Arial, sans-serif';
          ctx.fillStyle = "#ffffffff";
          ctx.fillRect(cursor[cursorKey].xPosition - 11, this._graphComponent.chartArea.top - 6, 20, 22);
          ctx.fillStyle = "#000000ff";
          ctx.fillText(cursor[cursorKey].display, cursor[cursorKey].xPosition - 11, this._graphComponent.chartArea.top + 12);
          // フォントを戻す
          ctx.font = '12px "Helvetica Neue", Helvetica, Arial, sans-serif';
          ctx.stroke();
        }

        ctx.restore();
      }
    };

    // ドラッグ可能なカーソル線の描画
    this.PLUGIN_DRAGGABLE_CURSOR_LINE = {
      id: 'dragger',
      beforeEvent: (chart, args) => {
        if (this.handleDrag(args.event, chart)) {
          args.changed = true;
          return;
        }
      }
    };

    // グリッド上部の足りない線を書き足す
    this.EXTEND_GRID = {
      id: "extendGrid",
      afterDraw(chart) {
        const { ctx, chartArea: { top }, scales: { x } } = chart;
        ctx.save();
        ctx.strokeStyle = 'rgba(169, 169, 169, 0.4)';
        ctx.lineWidth = 1;
        x.ticks.forEach((_, i) => {
          const xPos = x.getPixelForTick(i);
          ctx.beginPath();
          ctx.moveTo(xPos, top - 9);
          ctx.lineTo(xPos, top);
          ctx.stroke();
        });
        ctx.restore();
      }
    };
  }

  close() {
    this.removeCanvasListeners();

    this.currentTime = this.xMin; // 現在再生時刻
    this.markerLine = [];
    this.datasetList = [];
    this.baseTime = null;
    this.baseTimeOffset = null;

    // グラフのデータをクリアする
    this._graphData.data.datasets.forEach((dataset) => {
      dataset.data = [];
      dataset.displayValue = [];
    });

    // Chartインスタンスを削除
    if (this._graphComponent) {
      this._graphComponent.destroy();
      this._graphComponent = null;
    }
  }

  // 桁数を返すメソッド
  getDigits(number) {
    return Math.floor(Math.log10(number)) + 1;
  }

  // 太さを設定するメソッド
  getBorderWidth(borderWidth) {
    let rtnValue = NORMAL;
    switch (borderWidth) {
      case LINE_WIDTH.THIN:
        rtnValue = THIN;
        break;
      case LINE_WIDTH.THICK:
        rtnValue = THICK;
        break;
      default:
        rtnValue = NORMAL;
        break;
    }
    return rtnValue;
  }

  // スタイルを設定するメソッド
  getBorderDash(borderDash) {
    let rtnValue = NORMAL;
    switch (borderDash) {
      case 2:
        rtnValue = DASHED;
        break;
      case 3:
        rtnValue = DOTTED;
        break;
      default:
        rtnValue = SOLID;
        break;
    }
    return rtnValue;
  }

  // 監視する属性名のリストを返却する
  static get observedAttributes() {
    return ['id', 'item-name', 'xaxes-info', 'yaxes-info', 'index'];
  }

  // 監視する属性が設定・変更された際に呼び出される
  // _name 属性を変更し、レンダリングする
  // newValueに監視している属性の新しい値が設定される
  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case "id":
        this._id = newValue;
        break;

      case "item-name":
        this._itemName = newValue;
        break;

      case "xaxes-info":
        this._xAxesInfo = JSON.parse(newValue);
        break;

      case "yaxes-info":
        this._yAxesInfo = JSON.parse(newValue);
        break;

      case "index":
        this.index = parseInt(newValue) || undefined;
        break;
      default:
        break;
    }
  }

  initTime() {
    this.currentTime = dayjs(this.startTime);
  }

  /**
   * ズーム・パン可能な軸を設定する
   * @param {string} mode
   */
  setZoomPanMode(mode = DEFAULT_ZOOM_PAN_MODE) {
    this.zoomPanMode = mode;
  }

  setComponent() {
    // 画面から取得したx軸データリスト
    const xAxesInfoList = this._xAxesInfo;
    this.startTime = xAxesInfoList.startTime;
    this.rangeMs = xAxesInfoList.range;
    this.endTime = this.startTime + this.rangeMs;
    this.xMin = dayjs(this.startTime);
    this.xMax = dayjs(this.endTime);
    // 画面から取得したy軸データリスト
    const yAxesInfoList = Object.values(this._yAxesInfo);

    const graphContainer = document.createElement('div');
    graphContainer.setAttribute("id", `graphContainer` + this._id);
    graphContainer.setAttribute("class", "graph-container");
    graphContainer.setAttribute("data-testid", `graphContainer`); //test用
    // グラフコンテイナーのスタイル設定する
    graphContainer.style.width = "800";

    // グラフ描画用のcanvasを生成する
    const offsetParX = 0;
    const offsetParY = -0;
    const canParY = 1 - offsetParY;
    const canParX = 1 - offsetParX;
    const canvas = document.createElement('canvas');
    canvas.setAttribute("id", `canvas` + this._id);
    canvas.setAttribute("class", "graph-canvas");
    canvas.setAttribute("data-testid", `canvas`);//test用
    canvas.style.transform = "scale(" + canParX + "," + canParY + ")";
    canvas.style.transformOrigin = "top right";
    canvas.style.zIndex = 0;

    // グラフのデータセット配列
    // yAxesInfoをChart.JSデータフォーマットに合わせて格納する
    yAxesInfoList.forEach((yAxesInfo, index) => {
      // グラフ色。グラフ線の追加時に上書きする
      const color = yAxesInfo.color ? yAxesInfo.color : COLOR.DEFAULT;
      const dataset = {
        //Y軸ラベル
        label: yAxesInfo.id,
        yAxisID: 'yaxis_' + index,
        //Y軸データ(各データの時間に対応)
        data: [],
        displayValue: [],
        // ツールチップの色の背景色を設定(CITS)
        backgroundColor: color,
        // 下側の塗りつぶし色を設定しない(CITS)
        fill: false,
        //線の色
        borderColor: color,
        // 線の太さ
        borderWidth: this.getBorderWidth(yAxesInfo.width),
        // 線のスタイル
        borderDash: this.getBorderDash(yAxesInfo.style),
      };
      this.datasetList.push(dataset);
    });

    const data = {
      // データセット
      datasets: this.datasetList
    };

    const self = this;
    const graphData = {
      plugins: [this.PLUGIN_DESIGN, this.PLUGIN_CURRENT_LINE, this.PLUGIN_MARKER_LINE, this.EXTEND_GRID, this.PLUGIN_DRAGGABLE_CURSOR_LINE, zoomPlugin],
      type: "line",
      data,
      options: {
        clip: {
          // グラフの左右にはみ出した線やポインタは切り取る
          left: 0,
          right: 0,
          // マウスオーバー時のポインタが欠けないように上下のはみ出しは許容する
          top: PADDING_GRAPH_DISPLAY,
          bottom: PADDING_GRAPH_DISPLAY + 1
        },
        responsive: true,
        maintainAspectRatio: false,
        elements: {
          line: {
            tension: 0 // 0で直線、1でスムーズな曲線
          },
          point: {
            radius: 0, // ポイント描画を無効
            hitRadius: 20, // ヒット半径を設定
            hoverRadius: 6 // ホバー時の半径
          }
        },
        spanGaps: false, // ラインのセグメンテーション有効(HEV系AT対応にてグラフ線切る必要があったためfalseに変更)
        // アニメーション設定は使用しない
        animation: false,
        events: ['mousedown', 'mouseup', 'mousemove', 'mouseout'],
        plugins: {
          legend: {
            // 凡例の表示設定
            display: false
          },

          // カレントライン描画プラグインの設定
          currentLine: {
            enabled: false,
            xPosition: null
          },

          // マーカライン描画プラグインの設定
          markerLine: {
            enabled: false,
            xPositions: null
          },

          // カーソルライン描画プラグインの設定
          cursorLine: {
            enabled: false,
            ...DEFAULT_CURSOR_SETTINGS
          },

          // ツールチップのカスタマイズ
          tooltip: {
            callbacks: {
              // タイトル部
              title: function (tooltipItem) {
                // 経過時間
                const passedTime = self.getPassedTime(tooltipItem[0].parsed.x);
                if (passedTime === -1) {
                  return '';
                }

                // 日時
                const itemTime = tooltipItem[0].parsed.x + (self.baseTimeOffset || 0);
                const passedDate = UTC.add(itemTime).format('YYYY/MM/DD HH:mm:ss.SSS');

                return `${GraphMediator.getPassedTimeStr(passedTime, true)} [${passedDate}]`;
              },
              // データラベル部
              label: function (tooltipItem) {
                const targetYAxisInfo = self._yAxesInfo.find(yAxisInfo => tooltipItem.dataset.label === yAxisInfo.id);
                const targetData = tooltipItem.dataset.data.find(d => d.y === tooltipItem.raw.y);
                const displayValue = tooltipItem.dataset.displayValue &&
                  tooltipItem.dataset.displayValue.find(v => v.x === targetData.x);

                const tempLabelName = targetYAxisInfo.itemName ? targetYAxisInfo.itemName : '';
                const labelUnit = targetYAxisInfo.unit ? targetYAxisInfo.unit : '';
                const labelName = targetYAxisInfo.system ? `[${targetYAxisInfo.system}] ${tempLabelName}` : tempLabelName;

                return `${labelName} [${labelUnit}] : ${(displayValue && displayValue.y) || tooltipItem.raw.y}`;
              }
            }
          },

          // chartjs-plugin-annotation の設定
          annotation: {
            enter(ctx) {
              self.draggingElement = ctx.element;
            },
            leave() {
              if (!self.lastEvent) {
                self.draggingElement = null;
              }
            },
            annotations: {
              [c.CURSOR.A]: {
                id: "cursorALine",
                type: "line",
                mode: "vertical",
                scaleID: "x",
                borderColor: "green",
                borderWidth: 4,
                borderDash: [5, 5],
                display: false
              },
              [c.CURSOR.B]: {
                id: "cursorBLine",
                type: "line",
                mode: "vertical",
                scaleID: "x",
                borderColor: "green",
                borderWidth: 4,
                borderDash: [5, 5],
                display: false
              }
            }
          },
          zoom: {
            zoom: {
              wheel: {
                enabled: true,
              },
              drag: {
                enabled: true,
                threshold: 24,
                borderWidth: 0,
                backgroundColor: 'rgba(94, 94, 94, 0.2)',
              },
              mode: self.zoomPanMode,
              onZoomComplete(args) {
                self.onZoomPanComplete(args.chart, INTERACTION_TYPE.ZOOM);
              },
              onZoomStart({ event }) {
                if (event?.type === 'mousedown' && self.draggingElement) {
                  // カーソル上でのマウス押下は、カーソルドラッグを優先し、falseを返してabortする
                  return false;
                }
                return true;
              },
            },
            pan: {
              enabled: true,
              mode: self.zoomPanMode,
              modifierKey: 'ctrl',
              onPanComplete(args) {
                self.onZoomPanComplete(args.chart, INTERACTION_TYPE.PAN);
              }
            },
            limits: {
              // 最初に表示されたときのスケールを、ズーム・パンのリミットとして設定する
              x: { min: "original", max: "original" },
              y: { min: "original", max: "original" }
            }
          }
        },
        // 軸の設定
        scales: {
          // X軸
          x: {
            //軸ラベル表示
            title: {
              display: true,
            },
            type: "time",
            time: {
              // データをパースする形式
              parser: "HH:mm:ss",
              // 時間単位を強制的に定義した単位に変更
              unit: "second",
              // グリッドライン間のステップサイズ
              stepSize: this.stepSize,
              // 表示フォーマット
              displayFormats: {
                second: "HH:mm:ss"
              }
            },
            ticks: {
              // X軸ラベル表示文字列生成用コールバック
              callback: function (value) {
                const passedTime = self.getPassedTime(value);
                if (passedTime === -1) {
                  return '';
                }
                return GraphMediator.getPassedTimeStr(passedTime, false);
              },
              // x軸表示画面内の最小値
              min: this.xMin.format('HH:mm:ss'),
              // x軸表示画面内の最大値
              max: this.xMax.format('HH:mm:ss'),
              // スケールの最大表示数
              maxTicksLimit: 30,
              // ローテーション
              maxRotation: 60,
              minRotation: 0,
              // ラベルが重なる場合でも自動的にスキップしない
              autoSkip: false,
              // グラフとX軸ラベル間のパディング
              padding: 5
            },
            gridLines: {
              // グリッドラインの設定
              color: COLOR.GRID,
              drawTicks: false,
            },
            grid: {
              // メモリの長さ
              tickLength: 12,
            },
            // x軸表示画面内の最小値
            min: this.xMin.valueOf(),
            // x軸表示画面内の最大値
            max: this.xMax.valueOf()
          },
          // Y軸
          y: {
            ticks: {
              display: false,
              maxTicksLimit: 100
            },
            display: true
          }
        },
        layout: {
          padding: {
            top: 8,  // 上部外枠が見切れないようにする
            bottom: 10,
            right: 10 // 外枠がスクロールバーに被らないようにする
          }
        }
      }
    };

    // Y軸ラベルを線ごとに消す
    for (const data of this.datasetList) {
      graphData.options.scales[data.yAxisID] = {
        display: false
      };
    }

    this._graphData = graphData;
    // グラフコンポーネントを生成
    Chart.register(annotationPlugin);
    const graphComponent = new Chart(canvas, graphData);

    graphContainer.appendChild(canvas);
    this.parentElement.appendChild(graphContainer);
    // html反映後に高さ調整
    const graphContainerHeight = graphContainer.getBoundingClientRect().height;
    if (graphContainerHeight !== 0) {
      containerHeight = graphContainerHeight;
      canvas.setAttribute('height', containerHeight);
      canvas.style.height = containerHeight + 'px';
    }

    // グラフコンポーネントをセット
    this._graphComponent = graphComponent;

    // グラフのイベントリスナーを設定する
    this.addCanvasListeners(canvas);

    // yラベル設定
    this.setYlabel();
  }

  // 既に登録されている同一のコンポーネントを削除する
  deleteComponent() {
    this.removeCanvasListeners();

    // 既にグラフが登録されている場合、同じコンポーネントを生成しない
    const elementId = `graphContainer` + this._id;

    const isGraphRegistered = document.getElementById(elementId) ? true : false;
    const elementToRemove = document.getElementById(elementId);
    if (isGraphRegistered) {
      elementToRemove.parentNode.removeChild(elementToRemove);
    }
  }

  /**
   * グラフのイベントリスナーを設定する
   * @param {HTMLCanvasElement} canvas
   */
  addCanvasListeners(canvas) {
    this.removeCanvasListeners();

    if (!canvas) {
      return;
    }

    this.eventCanvas = canvas;
    this.eventCanvas.addEventListener('mousedown', this.onCanvasMouseDown);
    this.eventCanvas.addEventListener('mousemove', this.onCanvasMouseMove);
    this.eventCanvas.addEventListener('mouseup', this.onCanvasMouseUp);
    this.eventCanvas.addEventListener('mouseleave', this.onCanvasMouseLeave);
    this.eventCanvas.addEventListener('click', this.onCanvasClick);
    this.eventCanvas.addEventListener('dblclick', this.onCanvasDoubleClick);
  }

  /**
   * グラフのイベントリスナーを削除する
   */
  removeCanvasListeners() {
    if (!this.eventCanvas) {
      return;
    }

    clearTimeout(this.clickTimer);
    this.eventCanvas.removeEventListener('mousedown', this.onCanvasMouseDown);
    this.eventCanvas.removeEventListener('mousemove', this.onCanvasMouseMove);
    this.eventCanvas.removeEventListener('mouseup', this.onCanvasMouseUp);
    this.eventCanvas.removeEventListener('mouseleave', this.onCanvasMouseLeave);
    this.eventCanvas.removeEventListener('click', this.onCanvasClick);
    this.eventCanvas.removeEventListener('dblclick', this.onCanvasDoubleClick);
    this.eventCanvas = null;
    this.pointerDownPosition = null;
    this.ignoreNextClick = false;
  }

  /**
   * 経過時間の計算
   * @param {*} time データポイントのタイムスタンプ(ミリ秒)
   * @returns 経過時間(ミリ秒)
   */
  getPassedTime(time) {
    if (!this.baseTime) {
      return -1;
    }

    // 経過時間を計算
    const passedTime = time - this.baseTime;
    if (passedTime < 0) {
      return -1;
    }

    return passedTime;
  }

  setYlabel() {
    // y軸情報
    const yAxesInfoList = Object.values(this._yAxesInfo);
    //グラフコンテイナーエレメント
    const graphContainer = document.getElementById('graphContainer' + this._id);
    const graphArea = graphContainer.parentElement;

    // 再計測時はY軸ラベルの外枠は再利用する。
    const yLabelOuter = document.getElementById(`yLabelOuter${this._id}`) ?? document.createElement('div');
    while (yLabelOuter.firstChild) {
      // 言語切り換え対応のため子要素は一旦全て削除する。
      yLabelOuter.removeChild(yLabelOuter.firstChild);
    }

    yLabelOuter.setAttribute("class", "y-label-outer");
    yLabelOuter.setAttribute("id", `yLabelOuter${this._id}`);
    yLabelOuter.setAttribute("data-testid", "yLabelContainerOuter");

    const containers = {};
    for (const containerAttr of ["Max", "ItemName", "Min"]) {
      const container = document.createElement("div");
      container.setAttribute("id", `yLabelContainer${containerAttr}${this._id}`);
      container.setAttribute("data-testid", `yLabelContainer${containerAttr}`);
      yLabelOuter.appendChild(container);
      containers[containerAttr] = container;
    }

    graphArea.insertBefore(yLabelOuter, graphContainer);

    yAxesInfoList.forEach((item, index) => {
      for (const attrName of ["Max", "Min", "ItemName"]) {
        // Y軸ラベルに表示する文字列を設定する
        GraphMediator.setYLabelAttr(containers[attrName], attrName.charAt(0).toLocaleLowerCase() + attrName.slice(1), {
          id: this._id,
          index: index,
          systemName: item.system,
          itemName: item.itemName,
          unit: item.unit,
          maxDisp: item.maxDisp,
          max: item.max,
          minDisp: item.minDisp,
          min: item.min,
          color: item.color ? item.color : COLOR.DEFAULT,
        });
      }
    });
  }

  /**
   * Y軸ラベルに設定する文字列に成形する
   * @param {string} value Y軸ラベルに設定する値
   * @param {string} unit Y軸ラベルに設定する単位
   * @returns Y軸ラベルに設定する文字列
   */
  formatYLabelText(value) {
    const displayValue = value || value === 0 ? value : "-";
    return displayValue;
  }

  getComponent() {
    return this._graphComponent;
  }

  /**
   * 更新種別に応じて設定を更新し、再描画を行う。type未指定の場合は再描画のみ行う。
   * @param {UPDATE_TYPE} type 更新種別
   * @param {any} value 更新に使用する値
   */
  updateComponent(type = null, value = null, noUpdate = false) {
    if (!this._graphComponent) return;
    // X軸のMin/Maxの設定
    if (type === UPDATE_TYPE.X_MIN_MAX) {
      if (value.xMin && (value.xMin instanceof dayjs)) {
        const xMin = value.xMin.format('HH:mm:ss');
        this._graphComponent.options.scales.x.ticks.min = xMin;
        this._graphComponent.options.scales.x.min = value.xMin.valueOf();
      }
      if (value.xMax && (value.xMax instanceof dayjs)) {
        const xMax = value.xMax.format('HH:mm:ss');
        this._graphComponent.options.scales.x.ticks.max = xMax;
        this._graphComponent.options.scales.x.max = value.xMax.valueOf();
      }
    }

    // X軸のMin/Max/maxTicksLimitのリセット
    if (type === UPDATE_TYPE.X_TICKS) {
      // 表示領域の時間
      const timeRange = Math.floor((this.endTime - this.startTime) / 1000);
      // ticksLimitを調整(0:00:00の線を+1する)
      const ticksLimit = Math.floor(timeRange / this.stepSize) + 1;
      this._graphComponent.options.scales.x.ticks.min = '';
      this._graphComponent.options.scales.x.ticks.max = '';
      this._graphComponent.options.scales.x.ticks.maxTicksLimit = ticksLimit;
    }

    if (!noUpdate) {
      this.drawCurrentLine();
    }
  }

  // 現在再生時点とマーカラインを引く
  drawCurrentLine() {
    if (!this._graphComponent) return;
    // 現在値のラベルを更新する
    this.drawCurrentLabel();
    // 現在再生時点の線を引く
    this.drawCurrentLineMain();
    if (!this.dataAppending) {
      // データ投入完了後は、グラフのレスポンシブを再開する
      this._graphComponent.options.responsive = true;
      // マーカー位置を更新する
      this.updateMarker();
    }
    // マーカー線の描画
    this.drawSetMarkerMain();

    this._graphComponent.update("none");
  }

  /**
   * カレントラインの線を非表示にする
   */
  hideCurrentLine() {
    this._graphComponent.options.plugins.currentLine = {
      enabled: false,
      xPosition: null,
    };
  }

  // 現在再生時点の線を引く
  drawCurrentLineMain() {
    if (!this._graphComponent) return;
    // データ投入中の場合は処理しない
    if (this.dataAppending) {
      this._graphComponent.options.plugins.currentLine.enabled = false;
      return;
    }

    if (!this._graphComponent.scales.x) {
      return;
    }

    const xAxes = this._graphComponent.scales.x;
    const xPosition = xAxes.getPixelForValue(this.currentTime.valueOf());

    const { left, right } = this._graphComponent.chartArea || {};
    if (!Number.isFinite(left) || !Number.isFinite(right) || xPosition < left || xPosition > right) {
      // カレントラインの位置がグラフの表示領域外の場合、カレントラインを非表示にする
      this.hideCurrentLine();
      return;
    }

    this._graphComponent.options.plugins.currentLine = {
      enabled: true,
      xPosition,
    };
  }

  // マーカ線の描画
  drawSetMarkerMain() {
    if (!this._graphComponent) return;
    // データ投入中の場合は処理しない
    if (this.dataAppending) {
      this._graphComponent.options.plugins.markerLine.enabled = false;
      return;
    }

    const xPositions = [];

    // 保持しているマーカーを全て描画
    this.markerLine.forEach((setMarkerLine) => {
      // マーカーの色
      this._graphComponent.ctx.strokeStyle = 'red';
      // マーカー線の太さ
      this._graphComponent.ctx.lineWidth = 3;
      const xAxes = this._graphComponent.scales.x;
      if (setMarkerLine.xLine === 0 && xAxes) {
        const date = this.currentTime.valueOf();
        const xPosition = xAxes.getPixelForValue(date);
        xPositions.push(xPosition);
      } else {
        xPositions.push(setMarkerLine.xLine);
      }
    });

    this._graphComponent.options.plugins.markerLine = {
      enabled: true,
      xPositions,
    };

    this._graphComponent.options.plugins.cursorLine = {
      enabled: true,
      ...this.cursorLine
    };
  }


  /**
   * 凡例作成
   */
  setLegend() {
    if (!this._graphComponent) return;
    //グラフコンテイナーエレメント
    const graphContainer = document.getElementById('graphContainer' + this._id);
    // グラフ描画領域エレメント
    const canvas = document.getElementById('canvas' + this._id);
    // 凡例コンテイナーを生成する
    const legendContainer = document.createElement('p');
    legendContainer.setAttribute("id", `legendContainer` + this._graphComponent.id);
    legendContainer.setAttribute("data-testid", `legendContainer`); //UT向けコード埋め込み
    // 凡例のスタイルを設定する
    legendContainer.style.position = "absolute";
    legendContainer.style.left = "4%";
    legendContainer.style.top = "3%";

    Object.values(LEGEND_DEFS).forEach(legend => {
      const tr = document.createElement("tr");
      tr.setAttribute("id", "legendRow" + this._id + legend);
      tr.style.display = "flex";

      const dotTd = document.createElement("td");
      dotTd.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" height="6px" viewBox="0 0 24 24" width="6px"><path d="M0 0h24v24H0z" fill="none"/><path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2z"/></svg>`;
      dotTd.style.fontSize = "11px";

      const titleTd = document.createElement("td");
      titleTd.setAttribute("id", "legendTitle" + this._id + legend);
      titleTd.textContent = this.translatedValue[legend] || '';
      titleTd.style.fontSize = "11px";

      const valueTd = document.createElement("td");
      valueTd.setAttribute("id", "legendValue" + this._id + legend);
      valueTd.style.fontSize = "11px";

      tr.appendChild(dotTd);
      tr.appendChild(titleTd);
      tr.appendChild(valueTd);

      legendContainer.appendChild(tr);
      legendContainer.style.zIndex = 2;
    });

    // 子要素にする
    graphContainer.insertBefore(legendContainer, canvas);
  }

  /**
   * 凡例削除
   */
  deleteLegend() {
    if (!this._graphComponent) return;
    const legendContainer = document.getElementById(`legendContainer` + this._graphComponent.id);
    if (legendContainer) {
      legendContainer.remove();
    }
  }

  /**
   * 凡例の更新
   * @param {array} targetValues 対象範囲内のデータ
   * @param {array} displayValues 対象範囲内の表示値のデータ
   * @param {string} dataType 対象項目のデータタイプ
   * @param {string} color 対象項目の色
   * @param {boolean} isAFirst 値Aのタイムスタンプが値Bより先かいなか
   */
  updateLegend(targetValues, displayValues, dataType, color, isAFirst) {
    const NO_VALUE_TEXT = '-';

    function getDisplayValue(targetData) {
      if (!targetData) {
        return '';
      }
      // 対象タイムスタンプに表示用の翻訳値があれば、そちらを使用する
      const displayValueData = displayValues.length && displayValues.find(v => v.x === targetData.x);
      return displayValueData ? displayValueData.y : targetData.y;
    }

    function calcValueA() {
      const targetData = isAFirst ? targetValues[0] : targetValues[targetValues.length - 1];
      return getDisplayValue(targetData);
    }

    function calcValueB() {
      const targetData = isAFirst ? targetValues[targetValues.length - 1] : targetValues[0];
      return getDisplayValue(targetData);
    }

    function calcMax(yValues) {
      switch (dataType) {
        case "Value":
        case "decExStr":
        case "ACL2INC": {
          const maxValue = Math.max(...yValues);
          const targetData = targetValues.find(v => Number(v.y) === maxValue);
          return getDisplayValue(targetData);
        }
        default: {
          return NO_VALUE_TEXT;
        }
      }
    }

    function calcMin(yValues) {
      switch (dataType) {
        case "Value":
        case "decExStr":
        case "ACL2INC": {
          const minValue = Math.min(...yValues);
          const targetData = targetValues.find(v => Number(v.y) === minValue);
          return getDisplayValue(targetData);
        }
        default: {
          return NO_VALUE_TEXT;
        }
      }
    }

    function calcAverage(yValues) {
      switch (dataType) {
        case "Value":
        case "decExStr": {
          // ACL2INCは平均は表示しない
          return yValues.reduce((a, b) => a + b) / targetValues.length;
        }
        default:
          return NO_VALUE_TEXT;
      }
    }

    const LEGEND_DEFINITIONS = [
      { id: LEGEND_DEFS.MAX, valueFunc: calcMax },
      { id: LEGEND_DEFS.MIN, valueFunc: calcMin },
      { id: LEGEND_DEFS.AVG, valueFunc: calcAverage },
      { id: LEGEND_DEFS.VALUE_A, valueFunc: calcValueA },
      { id: LEGEND_DEFS.VALUE_B, valueFunc: calcValueB }
    ];

    const yValues = targetValues.map(v => Number(v.y));
    LEGEND_DEFINITIONS.forEach(legendDef => {
      const legendRow = document.getElementById(`legendRow` + this._id + legendDef.id);
      legendRow.style.color = color;

      const legendValue = document.getElementById(`legendValue` + this._id + legendDef.id);
      if (legendValue) {
        legendValue.textContent = ` : ${legendDef.valueFunc(yValues)}`;
      }
    });
  }

  /**
   * カーソル項目のデータセットを取得
   */
  findCursorDataSet() {
    if (!this._graphComponent?.data?.datasets) {
      return null;
    }
    if (this._graphComponent.data.datasets.length === 1) {
      return this._graphComponent.data.datasets[0];
    }
    return this._graphComponent.data.datasets.find(dataset => dataset.label === this.cursorItem) || this._graphComponent.data.datasets[0];
  }

  /**
   * カーソル項目のY軸情報を取得
   */
  findCursorYAxesInfo() {
    if (!this._yAxesInfo) {
      return null;
    }
    if (this._yAxesInfo.length === 1) {
      return this._yAxesInfo[0];
    }
    return this._yAxesInfo.find(yAxisInfo => this.cursorItem === yAxisInfo.id) || this._yAxesInfo[0];
  }

  /**
   * カーソルの凡例を描画
   */
  drawCursorLegend() {
    if (!this._graphComponent) return;
    const cursorATime = this.cursorLine[c.CURSOR.A].time;
    const cursorBTime = this.cursorLine[c.CURSOR.B].time;
    if (!cursorATime || !cursorBTime) {
      this.deleteLegend();
      return;
    }

    const cursorDataset = this.findCursorDataSet();
    const targetYAxisInfo = this.findCursorYAxesInfo();
    if (!cursorDataset || !targetYAxisInfo) {
      return;
    }

    if (!document.getElementById(`legendContainer` + this._graphComponent.id)) {
      this.setLegend();
    }

    const dataTimes = cursorDataset.data.map(d => d.x);

    // 2分探索で近い値のインデックスを求める
    const indexA = binarySearch(dataTimes, cursorATime);
    const indexB = binarySearch(dataTimes, cursorBTime);

    const isCursorAFirst = indexA <= indexB;

    const start = isCursorAFirst ? indexA : indexB;
    const end = isCursorAFirst ? indexB : indexA;

    // カーソル間のデータを抜き出す。カーソルが同じ地点であれば、その値だけを使用。
    const targetData = start !== end ? cursorDataset.data.slice(start, end + 1) : [cursorDataset.data[start]];

    this.updateLegend(targetData, cursorDataset.displayValue, targetYAxisInfo.dataType, targetYAxisInfo.color, isCursorAFirst);
  }

  // 現在値のラベルを更新する
  drawCurrentLabel() {
    // データ投入中は、データ追加時にsetValueForLegendsで凡例が描画されるため処理しない
    if (this.dataAppending || !this._graphComponent) {
      return;
    }
    this._graphComponent.ctx.strokeStyle = 'blue';
    this._graphComponent.ctx.lineWidth = 3;
  }

  graphSetMarker(time = null) {
    if (!this._graphComponent) return;
    let formatTime;
    if (time) {
      formatTime = dayjs(time).valueOf();
    } else {
      formatTime = this.currentTime.valueOf();
    }
    // if (this.markerLine.length > 0) {
    //   // マーカー登録ボタンが１回でも押されている場合先に描画
    //   this.drawSetMarker();
    // }
    const xScale = this._graphComponent.scales.x;
    if (!xScale) {
      // グラフ描画前の場合は0で設定し、描画時にupdateMarkerで位置を再設定する
      this.markerLine.push({ xLine: 0, time: formatTime });
      return;
    }
    const date = this.currentTime.valueOf();
    const verticalLineX = xScale.getPixelForValue(date);
    if (!this.markerLine.includes(formatTime)) {
      // マーカー登録ボタンが押下されたときの座標と時間を保持
      this.markerLine.push({ xLine: verticalLineX, time: formatTime });
    }
  }

  /**
   * annotation pluginのカーソルの値を更新する
   * @param {string} cursorType カーソル種別
   * @param {number} time カーソルを更新する時間
   */
  updateCursorTime(cursorType, time) {
    const annotation = this._graphComponent?.options?.plugins?.annotation?.annotations?.[cursorType];
    if (!annotation || !Number.isFinite(time)) {
      return;
    }
    annotation.value = time;
  }

  /**
   * カーソル追加
   * @param {string} cursorType カーソル種別
   * @param {number} time カーソルを追加する時間
   */
  graphSetCursor(cursorType, time = null) {
    if (!this._graphComponent) return;
    let formatTime;
    if (time) {
      formatTime = dayjs(time).valueOf();
    } else {
      formatTime = this.currentTime.valueOf();
    }
    // Annotation pluginのカーソルの値を更新。グラフ表示範囲外でも更新されるように、先に更新する
    this.updateCursorTime(cursorType, formatTime);

    const xScale = this._graphComponent.scales.x;

    if (!xScale) {
      // グラフ描画前の場合は0で設定し、描画時にupdateMarkerで位置を再設定する
      this.cursorLine[cursorType] = {
        xPosition: 0, time: formatTime, display: this.cursorLine[cursorType].display
      };
      return;
    }
    const verticalLineX = xScale.getPixelForValue(formatTime);
    // カーソル登録ボタンが押下されたときの座標と時間を保持
    this.cursorLine[cursorType] = {
      xPosition: verticalLineX, time: formatTime, display: this.cursorLine[cursorType].display
    };

    // ドラッグ可能カーソル線も移動
    const elements = annotationPlugin.getAnnotations(this._graphComponent);
    this.draggingElement = elements.find(element => element.options.id === cursorType);

    if (this.draggingElement && Number.isFinite(verticalLineX)) {
      this.moveDraggableLine(verticalLineX, this._graphComponent);
    }
    this.draggingElement = null;
    this._graphComponent.options.plugins.annotation.annotations[cursorType].display = true;

    this.drawCursorLegend();
  }

  /**
   * カーソル削除
   */
  clearCursor() {
    this.cursorLine = { ...DEFAULT_CURSOR_SETTINGS };
    this.deleteLegend();
  }

  /**
   * カーソル更新
   * @param {string} cursorItem カーソル対象に指定する項目ID(e.g. ABG_25ePF\t101A_1_all)
   */
  updateCursor(cursorItem) {
    this.cursorItem = cursorItem;
    this.drawCursorLegend();
  }

  graphDeleteMarker(markerInfo) {
    const index = this.markerLine.findIndex(obj => obj.time === markerInfo.markerTime);
    if (index > -1) {
      this.markerLine.splice(index, 1);
    }
    // マーカー線の描画
    this.drawCurrentLine();
  }

  updateMarker() {
    if (!this._graphComponent) return;
    const xScale = this._graphComponent.scales.x;
    if (!xScale) {
      return;
    }
    this.markerLine.forEach((marker) => {
      const verticalLineX = xScale.getPixelForValue(marker.time);
      marker.xLine = verticalLineX;
    });

    Object.keys(this.cursorLine).forEach((cursorType) => {
      const cursorTime = this.cursorLine[cursorType].time;
      if (!Number.isFinite(cursorTime)) {
        return;
      }
      // annotation pluginのカーソルの値を更新
      this.updateCursorTime(cursorType, cursorTime);

      const cursorVerticalLineX = xScale.getPixelForValue(cursorTime);
      if (!Number.isFinite(cursorVerticalLineX)) {
        return;
      }
      this.cursorLine[cursorType].xPosition = cursorVerticalLineX;

      if (!this.draggingElement) {
        const elements = annotationPlugin.getAnnotations(this._graphComponent);
        this.draggingElement = elements.find(element => element.options.id === cursorType);
        if (this.draggingElement && (!this.draggingElement.centerX || this.draggingElement.centerX < 0)) {
          this.moveDraggableLine(cursorVerticalLineX, this._graphComponent);
        }
        this.draggingElement = null;
      }
    });
  }

  /**
   * グラフのリサイズを実施。
   * 計測中はresponsiveをOFFにしているため、必要なタイミングで呼び出す
   */
  resize() {
    const graphContainer = document.getElementById(`graphContainer` + this._id);
    if (graphContainer) {
      this._graphComponent && this._graphComponent.resize(
        graphContainer.getBoundingClientRect().width,
        // x軸のラベル下の余白を調整する
        graphContainer.getBoundingClientRect().height + 25
      );
    } else {
      this._graphComponent && this._graphComponent.resize();
    }
  }

  /**
   * グラフに表示する翻訳済み文字列の設定
   * @param {object} translatedValue 翻訳済み文字列を値にもつObject
   */
  setTranslatedValue(translatedValue) {
    this.translatedValue = translatedValue;
  }

  validation() {
    // IDの必須項目/型チェック。HTMLのID属性は基本的に文字数上限がないため、文字数チェックはしない。
    if (!this._id) {
      throw new Error('id does not exist');
    }
    if (typeof this._id !== 'string') {
      throw new Error('id must be of type string');
    }
    // x軸情報の必須項目チェック
    if (!this._xAxesInfo) {
      throw new Error('xaxes-info does not exist');
    }
    // y軸情報の必須項目チェック
    if (!this._yAxesInfo) {
      throw new Error('yaxes-info does not exist');
    }
    // 開始時間の桁数チェック
    if (this._xAxesInfo.startTime && typeof this._xAxesInfo.startTime !== 'number') {
      throw new Error('startTime must be of type number');
    }
    if (this._xAxesInfo.startTime && this.getDigits(this._xAxesInfo.startTime) !== TIME_DIGITS) {
      throw new Error('startTime has a different number of digits');
    }
    // 範囲の型チェック
    if (this._xAxesInfo.range && typeof this._xAxesInfo.range !== 'number') {
      throw new Error('range must be of type number');
    }
    // y軸情報の項目チェック
    this._yAxesInfo.forEach((item) => {
      this.validateYAxesInfos(item);
    });
  }

  // eslint-disable-next-line complexity
  validateYAxesInfos(item) {
    // 項目名
    if (!item.itemName) {
      throw new Error('itemName does not exist');
    }
    /*
    if (item.itemName.length > MAX_CHAR || item.itemName.length < MIN_CHAR) {
      throw new Error('incorrect itemName length');
    }
    */
    if (typeof item.itemName !== 'string') {
      throw new Error('itemName must be of type string');
    }
    // 単位
    if (item.unit.length > MAX_CHAR) {
      throw new Error('incorrect unit length');
    }
    if (typeof item.unit !== 'string') {
      throw new Error('unit must be of type string');
    }
    // 最大
    if (item.max && typeof item.max !== 'number') {
      throw new Error('max must be of type number');
    }
    // 最小
    if (item.min && typeof item.min !== 'number') {
      throw new Error('min must be of type number');
    }
    //最大最小の比較
    if (item.max && item.min && item.max <= item.min) {
      throw new Error('max is less than min');
    }
    // 色
    if (item.color && typeof item.color !== 'string') {
      throw new Error('color must be of type string');
    }
    // 太さ
    if (('width' in item) && typeof item.width !== 'number') {
      throw new Error('width must be of type number');
    }
    if (('width' in item) && !Object.values(LINE_WIDTH).includes(item.width)) {
      throw new Error('width must be 1, 2, or 3');
    }
    // スタイル
    if (('style' in item) && typeof item.style !== 'number') {
      throw new Error('style must be of type number');
    }
    if (('style' in item) && !Object.values(STYLE).includes(item.style)) {
      throw new Error('style must be 1, 2, or 3');
    }
  }

  /**
 * グラフをデータ変更なしで再描画する
 */
  refreshGraph() {
    if (!this._graphComponent) {
      // 更新元オブジェクトが存在しない場合は再描画せず処理を終了する。
      return;
    }

    // アニメーションなしでグラフを再描画
    this._graphComponent.update('none');
  }

  moveDraggableLine(newX, chart) {
    if (!this._graphComponent) return;
    const xValue = chart.scales.x.getValueForPixel(newX);
    const cursorType = this.draggingElement.options.id;
    if (xValue < this._graphComponent.options.scales.x.min || xValue > this._graphComponent.options.scales.x.max) {
      // グラフの外に線がいかないように止める
      return;
    }
    this.draggingElement.x = newX;
    this.draggingElement.x2 = newX;
    this.draggingElement.centerX = newX;
    if (this.draggingElement.elements && this.draggingElement.elements.length) {
      for (const subEl of this.draggingElement.elements) {
        subEl.x = newX;
        subEl.x2 = newX;
        subEl.centerX = newX;
        subEl.bX = newX;
        this._graphComponent.options.plugins.annotation.annotations[cursorType].value = xValue;
      }
    }
  }

  drag(moveX, chart) {
    if (!this._graphComponent) return;
    const xValue = chart.scales.x.getValueForPixel(this.draggingElement.centerX + moveX);
    const cursorType = this.draggingElement.options.id;
    if (xValue < this._graphComponent.options.scales.x.min || xValue > this._graphComponent.options.scales.x.max) {
      // グラフの外に線がいかないように止める
      return;
    }

    this.draggingElement.x += moveX;
    this.draggingElement.x2 += moveX;
    this.draggingElement.centerX += moveX;
    if (this.draggingElement.elements && this.draggingElement.elements.length) {
      for (const subEl of this.draggingElement.elements) {
        subEl.x += moveX;
        subEl.x2 += moveX;
        subEl.centerX += moveX;
        subEl.bX += moveX;
        this._graphComponent.options.plugins.annotation.annotations[cursorType].value = xValue;
      }
      this.cursorLine[cursorType] = {
        xPosition: this.draggingElement.centerX, time: xValue, display: this.cursorLine[cursorType].display
      };
    }
    this.drawCursorLegend(this.usingColors);
    this.draggingCallback(this._id, cursorType, this.usingColors, xValue);
  }

  handleElementDragging(event, chart) {
    if (!this.lastEvent || !this.draggingElement) {
      return false;
    }
    const moveX = event.x - this.lastEvent.x;
    this.drag(moveX, chart);
    this.lastEvent = event;
    return true;
  }

  /**
   * カーソルドラッグ時のイベント処理
   * @param {object} event ドラッグイベント
   * @param {object} chart ドラッグ対象のグラフオブジェクト
   * @returns {boolean} ドラッグ処理を行った場合はtrue、そうでない場合はfalse
   */
  handleDrag(event, chart) {
    if (this.draggingElement) {
      switch (event.type) {
        case 'mousemove':
          return this.handleElementDragging(event, chart);
        case 'mouseout':
        case 'mouseup':
          this.lastEvent = null;
          this.draggingElement = null;
          break;
        case 'mousedown':
          if (this.isZoomingOrPanning()) {
            // ズームやパンの操作中はカーソル線のドラッグ処理を行わない
            return false;
          }
          // Ctrlキーが押されている場合はパンを優先し、カーソルドラッグを開始しない
          if (event.native?.ctrlKey) {
            return false;
          }
          this.lastEvent = event;
          break;
        default:
      }
    }
    return false;
  }

  /**
   * グラフのクリックイベントを処理する。
   * グラフ上をクリックしたときに、クリック位置のX軸の値をコールバック処理で通知する。
   * @param {object} event クリックイベント
   */
  handleGraphClick(event) {
    if (!this._graphComponent || this.dataAppending || this.draggingElement) {
      return;
    }
    const xScale = this._graphComponent.scales?.x;
    const chartArea = this._graphComponent.chartArea;
    if (!xScale || !chartArea) {
      return;
    }

    const rect = this._graphComponent.canvas.getBoundingClientRect();
    const x = (event.clientX - rect.left);
    const y = (event.clientY - rect.top);

    if (x < chartArea.left || x > chartArea.right || y < chartArea.top || y > chartArea.bottom) {
      return;
    }

    const clickedTimeMs = xScale.getValueForPixel(x);
    if (!Number.isFinite(clickedTimeMs)) {
      return;
    }

    if (typeof this.currentTimeSelectedCallback === 'function') {
      // クリックした時間を親に通知
      this.currentTimeSelectedCallback(this._id, clickedTimeMs);
    }
  }

  updateDraggingCallback(callback) {
    this.draggingCallback = callback;
  }

  /**
   * 現在選択位置の更新処理のコールバック関数を更新する
   * コールバック関数はGraphLibraryから設定する想定
   * @param {Function} callback
   */
  updateCurrentTimeSelectedCallback(callback) {
    this.currentTimeSelectedCallback = callback;
  }

  /**
   * ズーム・パンの同期処理のコールバック関数を更新する
   * コールバック関数はGraphLibraryから設定する想定
   * @param {Function} callback
   */
  updateZoomPanSyncCallback(callback) {
    this.zoomPanSyncCallback = callback;
  }

  /**
   * ズーム/パン状態の変更時に実行するコールバック関数を更新する
   * @param {Function} callback ズーム/パン状態変更時に実行するコールバック関数
   */
  updateZoomPanStateChangedCallback(callback) {
    this.zoomPanStateChangedCallback = callback;
  }

  /**
   * ダブルクリックによるズーム/パンリセットのコールバック関数を更新する
   * @param {Function} callback
   */
  updateResetZoomSyncCallback(callback) {
    this.resetZoomSyncCallback = callback;
  }

  /**
   * グラフのダブルクリック時にズーム/パンのリセットを実施する
   */
  handleDoubleClickResetZoom() {
    if (this.dataAppending) {
      return;
    }
    if (typeof this.resetZoomSyncCallback === 'function') {
      this.resetZoomSyncCallback(this._id);
      return;
    }

    this.resetZoom();
  }

  /**
   * 指定されたX/Y軸範囲をグラフに反映する
   * @param {number} xMin X軸の最小値(ms)
   * @param {number} xMax X軸の最大値(ms)
   * @param {object|null} yRanges Y軸の範囲。分割グラフはY軸のズーム不可のため指定は任意。
   */
  applyZoomPanRange(xMin, xMax, yRanges = null) {
    if (!this._graphComponent || !Number.isFinite(xMin) || !Number.isFinite(xMax)) {
      return;
    }

    const chart = this._graphComponent;
    if (typeof chart.zoomScale !== 'function') {
      // pluginが適用されていれば通常は発生しない。
      return;
    }

    // 同期処理によりズームやパンをしたグラフが、さらに同期通知しないよう、適用した範囲を保持する。
    // onZoomCompleteやonPanCompleteで保持値と現在のスケールを比較し、同期起因なら処理を止める。
    this.suppressedZoomPanRange = { xMin, xMax };

    // 直接min/maxの値を変更すると、limitの基準が変わってしまいズームアウトやパンが機能しなくなる。
    // PluginのAPI(zoomScale)を使うことでlimitの基準を維持したままズームやパンの状態を同期させる。
    chart.zoomScale('x', { min: xMin, max: xMax }, 'none');

    if (this.zoomPanMode === DEFAULT_ZOOM_PAN_MODE && yRanges && typeof yRanges === 'object') {
      for (const [yAxisId, yRange] of Object.entries(yRanges)) {
        if (!yRange || !Number.isFinite(yRange.min) || !Number.isFinite(yRange.max)) {
          continue;
        }
        chart.zoomScale(yAxisId, { min: yRange.min, max: yRange.max }, 'none');
      }
      // Y軸ラベルのMin/Maxを更新する
      this.updateYLabelByScale(chart);
    }

    // ズーム時に同期先もカレントライン描画
    this.drawCurrentLine();
  }

  /**
   * ズーム・パン完了イベントが手動操作によるものか、グラフ同期処理によるものかを判定する。
   * @param {object} chart イベント発生時のChart.jsオブジェクト
   * @returns {boolean} 同期通知をスキップする場合はtrue
   */
  consumeSyncSuppression(chart) {
    if (this.suppressedZoomPanRange === null) {
      return false;
    }

    if (!chart?.scales?.x) {
      // 判定できない場合も待機状態へ戻す
      this.suppressedZoomPanRange = null;
      return true;
    }

    // フラグだけで判定すると、イベント順序によっては同期適用直後のユーザー操作まで誤って抑制される。
    // そのため、適用した範囲とほぼ一致する場合のみ同期起因と判断する。
    const { min, max } = chart.scales.x;
    const { xMin, xMax } = this.suppressedZoomPanRange;
    // 同期元グラフのx値を別グラフへ適用する際、反映先グラフ側の内部計算でmin/maxが1ms程度ずれる場合がある。
    // 厳密一致では同期起因イベントの判定に失敗する可能性があるため、誤差を許容する。
    const isSyncedRange = Math.abs(min - xMin) <= 1 && Math.abs(max - xMax) <= 1;

    // 判定後は待機状態へ戻す
    this.suppressedZoomPanRange = null;
    return isSyncedRange;
  }

  /**
   * グラフのズームプラグインの有効/無効を切り替える
   * @param {boolean} enabled 有効にする場合はtrue、無効にする場合はfalse
   */
  setZoomPluginEnabled(enabled) {
    const chart = this._graphComponent;
    if (chart && chart.options.plugins.zoom) {
      // マウスホイールズーム機能の有効/無効
      if (chart.options.plugins.zoom.zoom && chart.options.plugins.zoom.zoom.wheel) {
        chart.options.plugins.zoom.zoom.wheel.enabled = enabled;
      }
      // ドラッグズーム機能の有効/無効
      if (chart.options.plugins.zoom.zoom && chart.options.plugins.zoom.zoom.drag) {
        chart.options.plugins.zoom.zoom.drag.enabled = enabled;
      }
      // パン機能の有効/無効
      if (chart.options.plugins.zoom.pan) {
        chart.options.plugins.zoom.pan.enabled = enabled;
      }
      chart.update("none");
    }
  }

  /**
   * グラフがズーム/パン状態か否か
   * @returns {boolean} ズーム/パン状態であればtrue、そうでなければfalse
   */
  isZoomedOrPanned() {
    const chart = this._graphComponent;
    if (typeof chart?.isZoomedOrPanned !== 'function') {
      return false;
    }
    return chart.isZoomedOrPanned();
  }

  /**
   * グラフがズーム/パン中か否か
   * @returns {boolean} ズームやパンの操作中であればtrue、そうでなければfalse
   */
  isZoomingOrPanning() {
    const chart = this._graphComponent;
    if (typeof chart?.isZoomingOrPanning !== 'function') {
      return false;
    }
    return chart.isZoomingOrPanning();
  }

  /**
   * ズーム・パン状態を初期表示へ戻す
   */
  resetZoom() {
    const chart = this._graphComponent;
    if (typeof chart?.resetZoom !== 'function') {
      return;
    }

    chart.resetZoom();
    chart.update('none');
    this.zoomPanStateChangedCallback(false);
  }

  /**
   * ズームやパンの状態に応じてY軸のラベルを更新する
   * @param {object} chart Chart.jsオブジェクト
   */
  updateYLabelByScale(chart) {
    if (!chart?.scales || !Array.isArray(this._yAxesInfo)) {
      return;
    }

    this._yAxesInfo.forEach((yAxes, index) => {
      const yAxisId = `yaxis_${index}`;
      const yScale = chart.scales[yAxisId];
      if (!yScale) {
        return;
      }

      if (!["Value", "decExStr", "ACL2INC"].includes(yAxes.dataType)) {
        // Min/Maxの表示対象以外は更新をスキップする
        return;
      }

      if (!Number.isInteger(yAxes.digitAfterDecimal)) {
        // DID表のdigitAfterDecimalが整数でない場合は更新をスキップする(DID表の定義ミス以外は発生しない想定)
        return;
      }

      for (const attr of ["max", "min"]) {
        const label = document.getElementById(`${attr}${this._id}${index}`);
        if (!label) {
          continue;
        }

        const rawValue = yScale[attr];
        // DID表の情報を使って小数点以下の桁数を調整する
        const roundedValue = fixNumberWithDecimal(rawValue, yAxes.digitAfterDecimal);
        label.textContent = this.formatYLabelText(roundedValue);
      }
    });
  }

  /**
   * ズーム・パン完了時の共通イベント処理
   * @param {object} chart イベント発生時のChart.jsオブジェクト
   * @param {string} interactionType 操作種別('zoom' | 'pan')
   */
  onZoomPanComplete(chart, interactionType) {
    // ズームやパンの結果に応じてY軸ラベルのMin/Maxを更新する
    this.updateYLabelByScale(chart);

    // ズーム時にカレントラインも描画
    this.drawCurrentLine();

    if (this.consumeSyncSuppression(chart)) {
      // 同期処理によるズーム・パン完了のコールバックであれば、これ以上の同期通知は不要のため処理を切り上げる。
      return;
    }

    if (!chart?.scales?.x) {
      return;
    }

    this.zoomPanSyncCallback(this._id, chart.scales.x.min, chart.scales.x.max);
    this.zoomPanStateChangedCallback(interactionType === INTERACTION_TYPE.PAN);
  }
}
